/**
 * AppointmentsPendientes — FASE 2: Cards de solicitudes con Confirmar, Reagendar, Cancelar
 * Referencia: 0257-Maqueta-Desconfundir-Usuario-Agenda
 */
import React, { useEffect } from "react";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ReplayIcon from "@material-ui/icons/Replay";
import CancelIcon from "@material-ui/icons/Cancel";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt";
import "moment/locale/en-gb";
import { useStyles } from "./styles";
import Chip from "@material-ui/core/Chip";
import { i18n } from "../../translate/i18n";

export default function AppointmentsPendientes({ appointments, loading, onConfirm, onReschedule, onCancel, updatingId }) {
  const classes = useStyles();

  useEffect(() => {
    const lang = (i18n.language || "es").split("-")[0];
    if (lang === "en") moment.locale("en-gb");
    else if (lang === "pt") moment.locale("pt");
    else moment.locale("es");
  }, [i18n.language]);

  const formatDateAndTime = (dt) => (dt ? moment(dt).format("LLLL") : "-");
  const detailLine = (a) => [
    a.confirmationCode ? `${i18n.t("appointmentsModule.pendientes.code")}: ${a.confirmationCode}` : null,
    a.durationMin ? `${a.durationMin} min` : null,
    a.modality ? i18n.t(`appointmentsModule.pendientes.modality.${a.modality}`, { defaultValue: a.modality }) : null,
    a.locationDetail || null,
    a.bookedByAi ? i18n.t("appointmentsModule.pendientes.bookedByAi") : null
  ].filter(Boolean).join(" · ");

  if (loading) {
    return <Typography color="textSecondary">{i18n.t("appointmentsModule.pendientes.loading")}</Typography>;
  }

  if (appointments.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Typography color="textSecondary">{i18n.t("appointmentsModule.pendientes.emptyHint")}</Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>{i18n.t("appointmentsModule.pendientes.emptySub")}</Typography>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {i18n.t("appointmentsModule.pendientes.intro")}
      </Typography>
      {appointments.map((a) => (
        <div key={a.id} className={classes.pendientesCard}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" }}>
            <div>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                {a.contactName || i18n.t("appointmentsModule.pendientes.noName")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {a.serviceName || i18n.t("appointmentsModule.pendientes.serviceFallback")}
                {a.professionalName && ` · ${i18n.t("appointmentsModule.pendientes.instructor", { name: a.professionalName })}`}
              </Typography>
              <div style={{ display: "flex", alignItems: "center", marginTop: 4 }}>
                <AccessTimeIcon fontSize="small" style={{ marginRight: 4, fontSize: 16 }} />
                <Typography variant="body2" style={{ fontWeight: 500 }}>{formatDateAndTime(a.appointmentDate)}</Typography>
              </div>
              {detailLine(a) && (
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }}>
                  {detailLine(a)}
                </Typography>
              )}
              {a.notes && (
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                  {i18n.t("appointmentsModule.pendientes.notes")}: {a.notes}
                </Typography>
              )}
            </div>
            <Chip label={i18n.t("appointmentsModule.pendientes.chipPending")} size="small" color="secondary" style={{ backgroundColor: "#ed6c02", color: "#fff" }} />
          </div>
          <div className={classes.pendientesCardActions}>
            <Tooltip title={i18n.t("appointmentsModule.pendientes.tooltipConfirm")}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<CheckCircleIcon />}
                onClick={() => onConfirm(a)}
                disabled={Boolean(updatingId)}
                aria-label={i18n.t("appointmentsModule.pendientes.confirm")}
              >
                {i18n.t("appointmentsModule.pendientes.confirm")}
              </Button>
            </Tooltip>
            <Tooltip title={i18n.t("appointmentsModule.pendientes.tooltipReschedule")}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ReplayIcon />}
                onClick={() => onReschedule(a)}
                disabled={Boolean(updatingId)}
                aria-label={i18n.t("appointmentsModule.pendientes.reschedule")}
              >
                {i18n.t("appointmentsModule.pendientes.reschedule")}
              </Button>
            </Tooltip>
            <Tooltip title={i18n.t("appointmentsModule.pendientes.tooltipCancel")}>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                startIcon={<CancelIcon />}
                onClick={() => onCancel(a)}
                disabled={Boolean(updatingId)}
                aria-label={i18n.t("appointmentsModule.pendientes.cancel")}
              >
                {i18n.t("appointmentsModule.pendientes.cancel")}
              </Button>
            </Tooltip>
          </div>
        </div>
      ))}
    </div>
  );
}
